import React, { useState, useEffect } from 'react';
import { Star, Search, Trash2, MessageSquare, Users, TrendingUp, RefreshCw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../services/api';

export function AdminReviewsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedId, setExpandedId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/all-ratings');
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (ratingId) => {
    if (!window.confirm('Bewertung wirklich löschen? Der Fahrer-Durchschnitt wird neu berechnet.')) return;
    setDeleting(ratingId);
    try {
      await api.delete(`/admin/rating/${ratingId}`);
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Fehler'); }
    finally { setDeleting(null); }
  };

  const ratings = data?.ratings || [];

  const filtered = ratings.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(q) ||
           (r.driver_name || '').toLowerCase().includes(q) ||
           (r.comment || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'newest') return (b.created_at || '').localeCompare(a.created_at || '');
    if (sortBy === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
    if (sortBy === 'highest') return (b.average || 0) - (a.average || 0);
    if (sortBy === 'lowest') return (a.average || 0) - (b.average || 0);
    return 0;
  });

  const StarDisplay = ({ value, size = 14 }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={size} className={s <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-reviews-tab">
      {/* Stats */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reviews-stats">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <Star size={16} className="text-amber-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Bewertungen</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-reviews">{data.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <TrendingUp size={16} className="text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Durchschnitt</span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-slate-800" data-testid="stat-avg-rating">{data.avg_overall}</p>
              <StarDisplay value={data.avg_overall} size={12} />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                <Star size={16} className="text-orange-600 fill-orange-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">5-Sterne</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-five-star">{data.five_star_count}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <MessageSquare size={16} className="text-blue-600" />
              </div>
              <span className="text-xs font-medium text-slate-500">Mit Kommentar</span>
            </div>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-with-comment">{data.with_comment_count}</p>
          </div>
        </div>
      )}

      {/* Search & Sort */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Suche nach Kunde, Fahrer oder Kommentar..."
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="reviews-search-input"
            />
          </div>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="reviews-sort-select"
          >
            <option value="newest">Neueste zuerst</option>
            <option value="oldest">Älteste zuerst</option>
            <option value="highest">Beste zuerst</option>
            <option value="lowest">Schlechteste zuerst</option>
          </select>
          <button onClick={loadData} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="reviews-refresh-btn">
            <RefreshCw size={16} className={`text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Ratings List */}
      <div className="bg-white rounded-xl border border-slate-100" data-testid="reviews-list">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-bold text-sm text-slate-800">Alle Bewertungen ({sorted.length})</h2>
        </div>

        {loading && !data ? (
          <div className="p-10 flex justify-center">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="p-6 text-sm text-slate-400 text-center">
            {search ? 'Keine Bewertungen gefunden' : 'Noch keine Bewertungen vorhanden'}
          </p>
        ) : (
          <div className="divide-y divide-slate-50">
            {sorted.map(r => {
              const isExpanded = expandedId === r.id;
              return (
                <div key={r.id} className="hover:bg-slate-50/50 transition-colors" data-testid={`review-${r.id}`}>
                  {/* Main Row */}
                  <div
                    className="px-5 py-3 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : r.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StarDisplay value={r.average || 0} size={14} />
                        <span className="text-sm font-semibold text-slate-800">{(r.average || 0).toFixed(1)}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>Kunde: <span className="font-medium text-slate-700">{r.customer_name}</span></span>
                        <span>Fahrer: <span className="font-medium text-slate-700">{r.driver_name}</span></span>
                      </div>
                    </div>
                    <div className="text-right shrink-0 flex items-center gap-3">
                      <div>
                        <p className="text-xs text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString('de-DE') : '-'}</p>
                        {r.comment && <MessageSquare size={12} className="text-blue-400 ml-auto mt-0.5" />}
                      </div>
                      {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="px-5 pb-4 pt-0">
                      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                        {/* Category Ratings */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          {[
                            { key: 'punctuality', label: 'Pünktlichkeit' },
                            { key: 'cleanliness', label: 'Sauberkeit' },
                            { key: 'friendliness', label: 'Freundlichkeit' },
                            { key: 'driving_style', label: 'Fahrstil' },
                          ].map(cat => (
                            <div key={cat.key} className="text-center">
                              <p className="text-xs text-slate-500 mb-1">{cat.label}</p>
                              <div className="flex items-center justify-center gap-1">
                                <StarDisplay value={r[cat.key] || 0} size={12} />
                                <span className="text-xs font-semibold text-slate-700">{r[cat.key] || 0}</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Comment */}
                        {r.comment && (
                          <div className="bg-white rounded-lg p-3 border border-slate-200">
                            <p className="text-xs font-semibold text-slate-500 mb-1">Kommentar</p>
                            <p className="text-sm text-slate-700">{r.comment}</p>
                          </div>
                        )}

                        {/* Meta */}
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <div className="flex items-center gap-3">
                            <span>Transfer: {r.transfer_id?.slice(0, 8)}...</span>
                            <span>E-Mail: {r.customer_email || '-'}</span>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                            disabled={deleting === r.id}
                            className="flex items-center gap-1 px-2 py-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                            data-testid={`delete-review-${r.id}`}
                          >
                            {deleting === r.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            Löschen
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
